import { defineStore } from 'pinia';
import type { Socket } from 'socket.io-client';
import { computed, ref } from 'vue';
import type {
  PdsItemsUpdate,
  PdsLineAdjustment,
  PdsOrdersGateway,
} from '../gateway';
import { formatPickup, formatTime } from '../format';
import { orderLink } from '../orderLink';
import { resolveGateway } from '../resolve-gateway';
import {
  TEXT_MAX_LENGTH,
  type PdsCancellation,
  type PdsOrder,
  type PdsRefusal,
  type PdsStage,
  type PdsStageFilter,
} from '../types';

export const READY_UNDO_SECONDS = 8;
const SMS_NOTICE_MS = 6_000;
const MINUTE = 60_000;
const ACTIVE_STAGES: PdsStage[] = ['new', 'preparing', 'ready'];
export const ACTION_ERROR =
  'L’action n’a pas pu être enregistrée. Vérifiez le réseau, puis réessayez.';

export type PdsAnnouncement = {
  id: number;
  tone: 'success' | 'new' | 'info' | 'alert';
  title: string;
  text: string;
  // La commande dont parle la notification : « Voir » l'ouvre.
  orderId?: string;
  // Où « Voir » mène à la place de la commande : sa fiche, dont « retour » ramène à la liste des remboursées et refusées.
  to?: string;
};

// La liste des commandes remboursées et refusées, où atterrit une commande refusée ou annulée.
const CANCELLED_LIST = '/journal?statut=cancelled';

const ANNOUNCEMENT_MS = 4_000;

// Ce que dit la notification quand une commande change de colonne.
const STAGE_TEXT: Partial<Record<PdsStage, string>> = {
  preparing: 'Passée en préparation.',
  ready: 'Prête.',
  handed: 'Remise au client.',
  refused: 'Refusée.',
};

export type PdsLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export type PdsReadyToast =
  | { kind: 'undo'; order: PdsOrder; secondsLeft: number }
  | { kind: 'sent'; order: PdsOrder };

// Le serveur ne garde qu'un motif et l'exige non vide : les motifs cochés, « Sans motif » compris.
const reasonOf = ({ reasons }: PdsRefusal) =>
  reasons.join(', ').slice(0, TEXT_MAX_LENGTH);

export const createPdsOrdersStore = (id: string, gateway: PdsOrdersGateway) =>
  defineStore(id, () => {
    const orders = ref<PdsOrder[]>([]);
    const now = ref(Date.now());
    const stageFilter = ref<PdsStageFilter>('all');
    const loadStatus = ref<PdsLoadStatus>('idle');
    const actionError = ref<string | null>(null);
    const undoWindow = ref<{ orderId: string; secondsLeft: number } | null>(
      null,
    );
    const smsNoticeOrderId = ref<string | null>(null);
    const announcement = ref<PdsAnnouncement | null>(null);
    let announcementSeq = 0;
    let announcementTimer: ReturnType<typeof setTimeout> | undefined;
    const readyRequestId = ref<string | null>(null);
    let undoTimer: ReturnType<typeof setInterval> | undefined;
    let noticeTimer: ReturnType<typeof setTimeout> | undefined;

    const byId = (orderId: string) =>
      orders.value.find((order) => order.id === orderId);

    const inStage = (stage: PdsStage) => {
      const list = orders.value.filter((order) => order.stage === stage);
      return stage === 'ready'
        ? list.sort((a, b) => (b.readyAt ?? 0) - (a.readyAt ?? 0))
        : list;
    };

    const isLate = (order: PdsOrder) =>
      (order.stage === 'new' || order.stage === 'preparing') &&
      order.dueAt < now.value;

    const lateMinutes = (order: PdsOrder) =>
      Math.max(0, Math.floor((now.value - order.dueAt) / MINUTE));

    const remainingCount = (order: PdsOrder) =>
      order.items.filter((item) => !item.prepared).length;

    const newOrders = computed(() => inStage('new'));
    const preparingOrders = computed(() => inStage('preparing'));
    const readyOrders = computed(() => inStage('ready'));
    const activeCount = computed(
      () => orders.value.filter((o) => ACTIVE_STAGES.includes(o.stage)).length,
    );
    const lateOrders = computed(() =>
      orders.value.filter(isLate).sort((a, b) => a.dueAt - b.dueAt),
    );

    const readyToast = computed<PdsReadyToast | null>(() => {
      if (undoWindow.value) {
        const order = byId(undoWindow.value.orderId);
        if (order)
          return {
            kind: 'undo',
            order,
            secondsLeft: undoWindow.value.secondsLeft,
          };
      }
      const sent = smsNoticeOrderId.value && byId(smsNoticeOrderId.value);
      return sent ? { kind: 'sent', order: sent } : null;
    });

    const dismissAnnouncement = () => {
      clearTimeout(announcementTimer);
      announcement.value = null;
    };

    // Une seule notification à la fois : la plus récente remplace la précédente.
    const announce = (
      tone: PdsAnnouncement['tone'],
      title: string,
      text: string,
      orderId?: string,
      to?: string,
    ) => {
      clearTimeout(announcementTimer);
      announcement.value = {
        id: ++announcementSeq,
        tone,
        title,
        text,
        orderId,
        to,
      };
      announcementTimer = setTimeout(dismissAnnouncement, ANNOUNCEMENT_MS);
    };

    const orderTitle = (order: PdsOrder) =>
      `#${order.orderNumber ?? order.number} · ${order.label}`;

    // Une commande a changé de colonne ailleurs (autre poste, serveur) : on le dit.
    const announceMove = (order: PdsOrder, previous: PdsStage) => {
      const text = STAGE_TEXT[order.stage];
      if (order.stage === previous || !text) return;
      announce('info', orderTitle(order), text, order.id);
    };

    // Une action de ce poste que le serveur a retenue : notification de succès.
    const confirmed = (order: PdsOrder) => {
      announce(
        'success',
        orderTitle(order),
        STAGE_TEXT[order.stage]!,
        order.id,
        order.stage === 'refused'
          ? orderLink(order.id, CANCELLED_LIST)
          : undefined,
      );
    };

    // Pendant qu'une action de la commande est en vol, les lectures du serveur sont ignorées :
    // elles écraseraient un état plus récent que celui qu'elles décrivent.
    const inFlight = new Map<string, number>();

    // Relit la commande : la colonne reflète ce que le serveur a vraiment retenu.
    const refresh = async (orderId: string) => {
      const fresh = await gateway.fetchOrder(orderId).catch((): null => null);
      if (fresh) upsert(fresh);
    };

    // Action optimiste : l'écran bouge tout de suite, revient en arrière si le serveur refuse,
    // puis relit la commande une fois la dernière action en vol terminée.
    const sync = async (
      orderId: string,
      request: () => Promise<void>,
      rollback: () => void,
      onSaved?: () => void,
    ) => {
      inFlight.set(orderId, (inFlight.get(orderId) ?? 0) + 1);
      let saved = true;
      try {
        await request();
        onSaved?.();
      } catch {
        saved = false;
        rollback();
        actionError.value = ACTION_ERROR;
      } finally {
        const left = (inFlight.get(orderId) ?? 1) - 1;
        if (left > 0) inFlight.set(orderId, left);
        else {
          inFlight.delete(orderId);
          void refresh(orderId);
        }
      }
      return saved;
    };

    const dismissActionError = () => {
      actionError.value = null;
    };

    const fetchAll = async () => {
      orders.value = await gateway.loadOrders();
    };

    const load = async () => {
      if (loadStatus.value === 'loading' || loadStatus.value === 'ready')
        return;
      loadStatus.value = 'loading';
      try {
        await fetchAll();
        loadStatus.value = 'ready';
      } catch {
        loadStatus.value = 'error';
      }
    };

    const retryLoad = async () => {
      loadStatus.value = 'idle';
      await load();
    };

    // Après une coupure du réseau, des événements ont pu être manqués : on relit tout, sans masquer l'écran.
    const resync = async () => {
      const pending = undoWindow.value && byId(undoWindow.value.orderId);
      try {
        const fresh = await gateway.loadOrders();
        orders.value = pending
          ? [...fresh.filter((o) => o.id !== pending.id), pending]
          : fresh;
      } catch {
        // L'écran garde son dernier état connu ; le prochain événement le corrigera.
      }
    };

    const tick = () => {
      now.value = Date.now();
    };

    const receiveCustomerCancellation = (orderId: string, at = Date.now()) => {
      const order = byId(orderId);
      if (!order || !ACTIVE_STAGES.includes(order.stage)) return;
      order.stage = 'cancelled';
      order.cancelledAt = at;
      order.cancelAcknowledged = false;
    };

    const upsert = (incoming: PdsOrder) => {
      const existing = byId(incoming.id);
      if (!existing) {
        if (!ACTIVE_STAGES.includes(incoming.stage)) return;
        // En tête, comme le serveur trie (le plus récent d'abord) : en bas d'une colonne qui défile, elle resterait hors de l'écran.
        orders.value.unshift(incoming);
        if (incoming.stage === 'new') {
          announce(
            'new',
            'Nouvelle commande',
            `${orderTitle(incoming)} · retrait ${formatTime(incoming.dueAt)}`,
            incoming.id,
          );
        }
        return;
      }
      // Pendant les 8 s d'annulation, l'état local fait foi : le serveur n'a encore rien reçu.
      if (undoWindow.value?.orderId === incoming.id) return;
      if (inFlight.has(incoming.id)) return;
      if (
        ACTIVE_STAGES.includes(existing.stage) &&
        incoming.stage === 'cancelled'
      ) {
        receiveCustomerCancellation(incoming.id, incoming.cancelledAt);
        announce(
          'alert',
          orderTitle(existing),
          'Annulée par le client.',
          existing.id,
        );
        return;
      }
      const previous = existing.stage;
      // Les lignes gardent leur place à l'écran : la relecture du serveur ne garantit pas le même ordre, et une ligne
      // qui saute sous le doigt après un coche fait cocher la mauvaise.
      const rank = new Map(existing.items.map((item, i) => [item.id, i]));
      incoming.items.sort(
        (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity),
      );
      Object.assign(existing, incoming);
      announceMove(existing, previous);
    };

    const applyItems = ({ orderId, stage, items }: PdsItemsUpdate) => {
      const order = byId(orderId);
      if (!order) return;
      items.forEach(({ id: itemId, prepared }) => {
        const item = order.items.find((i) => i.id === itemId);
        if (item) item.prepared = prepared;
      });
      if (
        undoWindow.value?.orderId !== orderId &&
        ACTIVE_STAGES.includes(order.stage) &&
        ACTIVE_STAGES.includes(stage)
      ) {
        const previous = order.stage;
        order.stage = stage;
        announceMove(order, previous);
      }
    };

    const bindSocket = (socket: Socket) => {
      const unsubscribe = gateway.subscribe(socket, {
        onOrder: upsert,
        onItems: applyItems,
      });
      socket.io.on('reconnect', resync);
      return () => {
        unsubscribe();
        socket.io.off('reconnect', resync);
      };
    };

    const markItem = (orderId: string, itemId: string, prepared: boolean) => {
      const item = byId(orderId)?.items.find((i) => i.id === itemId);
      if (!item || item.prepared === prepared) return;
      item.prepared = prepared;
      void sync(
        orderId,
        () => gateway.setItemPrepared(orderId, itemId, prepared),
        () => {
          item.prepared = !prepared;
        },
      );
    };

    const toggleItem = (orderId: string, itemId: string) => {
      const item = byId(orderId)?.items.find((i) => i.id === itemId);
      if (item) markItem(orderId, itemId, !item.prepared);
    };

    // Le volet « ce qu'il faut préparer » coche des lignes de plusieurs commandes : le serveur ne les accepte que
    // d'une commande en préparation, donc rien ne part pour une autre.
    const setItemsPrepared = (
      orderId: string,
      itemIds: string[],
      prepared: boolean,
    ) => {
      if (byId(orderId)?.stage !== 'preparing') return;
      itemIds.forEach((itemId) => markItem(orderId, itemId, prepared));
    };

    const accept = (orderId: string) => {
      const order = byId(orderId);
      if (order?.stage !== 'new') return;
      order.stage = 'preparing';
      void sync(
        orderId,
        () => gateway.setStage(orderId, 'preparing'),
        () => {
          order.stage = 'new';
        },
        () => confirmed(order),
      );
    };

    const refuse = (orderId: string, refusal: PdsRefusal) => {
      const order = byId(orderId);
      if (order?.stage !== 'new') return;
      order.stage = 'refused';
      order.refusal = refusal;
      void sync(
        orderId,
        () =>
          gateway.setStage(
            orderId,
            'refused',
            reasonOf(refusal),
            refusal.message,
          ),
        () => {
          order.stage = 'new';
          delete order.refusal;
        },
        () => confirmed(order),
      );
    };

    // Argent en jeu : rien d'optimiste, l'écran n'avance que si le serveur a dit oui.
    const sendMoneyAction = async (
      orderId: string,
      request: () => Promise<void>,
    ) => {
      inFlight.set(orderId, (inFlight.get(orderId) ?? 0) + 1);
      try {
        await request();
        return true;
      } catch {
        actionError.value = ACTION_ERROR;
        return false;
      } finally {
        const left = (inFlight.get(orderId) ?? 1) - 1;
        if (left > 0) inFlight.set(orderId, left);
        else inFlight.delete(orderId);
      }
    };

    const adjustLines = async (
      orderId: string,
      adjustments: PdsLineAdjustment[],
      clientMessage?: string,
      reasons?: string[],
    ) => {
      const order = byId(orderId);
      if (order?.stage !== 'preparing') return false;
      const saved = await sendMoneyAction(orderId, () =>
        gateway.adjustLines(
          orderId,
          adjustments,
          clientMessage,
          reasons && reasonOf({ reasons }),
        ),
      );
      if (!saved) return false;
      await refresh(orderId);
      announce(
        'success',
        orderTitle(order),
        'Commande modifiée, le client est prévenu. La différence est remboursée.',
        order.id,
      );
      return true;
    };

    const loadPickupChoices = (date: string) => gateway.loadPickupChoices(date);

    // Rien d'optimiste : le serveur peut refuser l'heure (créneau plein, jour fermé).
    const reschedule = async (orderId: string, pickupTime: number) => {
      const order = byId(orderId);
      if (!order || !ACTIVE_STAGES.includes(order.stage)) return false;
      const saved = await sendMoneyAction(orderId, () =>
        gateway.changePickupTime(orderId, pickupTime),
      );
      if (!saved) return false;
      await refresh(orderId);
      announce(
        'success',
        orderTitle(order),
        `Retrait déplacé à ${formatPickup(pickupTime)}, le client est prévenu.`,
        order.id,
      );
      return true;
    };

    const cancelOrder = async (
      orderId: string,
      cancellation: PdsCancellation,
    ) => {
      const order = byId(orderId);
      if (!order || !ACTIVE_STAGES.includes(order.stage)) return false;
      const saved = await sendMoneyAction(orderId, () =>
        gateway.cancelOrder(orderId, {
          reason: reasonOf(cancellation),
          clientMessage: cancellation.message,
        }),
      );
      if (!saved) return false;
      announce(
        'success',
        orderTitle(order),
        'Annulée, remboursement intégral parti.',
        order.id,
        orderLink(order.id, CANCELLED_LIST),
      );
      orders.value = orders.value.filter((o) => o.id !== orderId);
      return true;
    };

    const dismissNotice = () => {
      clearTimeout(noticeTimer);
      smsNoticeOrderId.value = null;
    };

    // Le serveur prévient le client dès qu'il reçoit « prête » : l'appel ne part qu'à la fin des 8 s.
    const finalizeReady = () => {
      const pending = undoWindow.value;
      const order = pending && byId(pending.orderId);
      if (!pending || !order) return;
      clearInterval(undoTimer);
      undoWindow.value = null;
      void sync(
        order.id,
        () => gateway.setStage(order.id, 'ready'),
        () => {
          order.stage = 'preparing';
          delete order.readyAt;
        },
      ).then((saved) => {
        if (!saved) return;
        smsNoticeOrderId.value = order.id;
        noticeTimer = setTimeout(dismissNotice, SMS_NOTICE_MS);
      });
    };

    const markReady = (orderId: string) => {
      const order = byId(orderId);
      if (order?.stage !== 'preparing') return;

      finalizeReady();
      dismissNotice();
      order.stage = 'ready';
      order.readyAt = Date.now();
      undoWindow.value = { orderId, secondsLeft: READY_UNDO_SECONDS };
      undoTimer = setInterval(() => {
        if (!undoWindow.value) return;
        undoWindow.value.secondsLeft -= 1;
        if (undoWindow.value.secondsLeft <= 0) finalizeReady();
      }, 1_000);
    };

    const requestReady = (orderId: string) => {
      if (byId(orderId)?.stage === 'preparing') readyRequestId.value = orderId;
    };

    const cancelReadyRequest = () => {
      readyRequestId.value = null;
    };

    const confirmReady = () => {
      if (readyRequestId.value) markReady(readyRequestId.value);
      readyRequestId.value = null;
    };

    const undoReady = () => {
      const pending = undoWindow.value;
      const order = pending && byId(pending.orderId);
      if (!pending || !order) return;
      clearInterval(undoTimer);
      undoWindow.value = null;
      order.stage = 'preparing';
      delete order.readyAt;
    };

    const handOver = (orderId: string) => {
      const order = byId(orderId);
      if (order?.stage !== 'ready') return;
      order.stage = 'handed';
      void sync(
        orderId,
        () => gateway.setStage(orderId, 'handed'),
        () => {
          order.stage = 'ready';
        },
        () => confirmed(order),
      );
    };

    const acknowledgeCancellation = (orderId: string) => {
      const order = byId(orderId);
      if (order) order.cancelAcknowledged = true;
    };

    const stopTimers = () => {
      clearInterval(undoTimer);
      clearTimeout(noticeTimer);
      clearTimeout(announcementTimer);
    };

    return {
      orders,
      now,
      stageFilter,
      loadStatus,
      actionError,
      announcement,
      newOrders,
      preparingOrders,
      readyOrders,
      activeCount,
      lateOrders,
      readyToast,
      readyRequestId,
      byId,
      isLate,
      lateMinutes,
      remainingCount,
      load,
      retryLoad,
      bindSocket,
      dismissActionError,
      dismissAnnouncement,
      tick,
      toggleItem,
      setItemsPrepared,
      accept,
      refuse,
      adjustLines,
      loadPickupChoices,
      reschedule,
      cancelOrder,
      markReady,
      requestReady,
      cancelReadyRequest,
      confirmReady,
      undoReady,
      sendReadyNow: finalizeReady,
      dismissNotice,
      handOver,
      receiveCustomerCancellation,
      acknowledgeCancellation,
      stopTimers,
    };
  });

export const usePdsOrdersStore = createPdsOrdersStore(
  'pds-orders',
  resolveGateway(),
);
